import { useState, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import { Container, Row, Col, Form } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useTranslation } from 'react-i18next';
import MainNav from '@/components/layout/AppNavbar';
import IdolCard from '@/components/ui/IdolCard';
import Pagination from '@/components/ui/Pagination';
import IdolFilterSidebar from '../components/IdolFilterSidebar';
import IdolFilterModal from '../components/IdolFilterModal';
import placeholder_portrait_200x200 from '@/assets/imgs/placeholders/portrait_200x200.jpg';
import placeholder_cover_1600x700 from '@/assets/imgs/placeholders/cover_1600x700.jpg';
import Axios from '@/shared/api/axios';
import Loading from '@/components/ui/Loading';
import StatusModal from '@/components/ui/StatusModal';
import { useDebouncedSearchInput } from '@/hooks/useDebouncedSearchInput';

const IdolsPage = () => {
  const { t } = useTranslation();
  const idolId = useSelector((state) => state.user.idolId);
  const [searchParams, setSearchParams] = useSearchParams();
  const [showFilterModal, setShowFilterModal] = useState(false);

  // API 非成功狀態碼統一由 StatusModal 顯示（含攔截器附加的 HTTP_xxx）
  const statusModalRef = useRef(null);
  const [currentStatusCode, setCurrentStatusCode] = useState('');

  // 從 URL 讀取篩選參數
  const currentPage = parseInt(searchParams.get('page') || '1');
  const keyword = searchParams.get('keyword') || '';
  const isOriginal = searchParams.get('is_original') || '';
  const isNsfw = searchParams.get('is_nsfw') || '';
  const ageMin = parseInt(searchParams.get('age_min') || '1');
  const ageMax = parseInt(searchParams.get('age_max') || '100');

  // 多選參數（react-select 格式）
  const gender = searchParams.getAll('gender');
  const sexualOrientation = searchParams.getAll('sexual_orientation');
  const relationshipStatus = searchParams.getAll('relationship_status');
  const preferences = searchParams.getAll('preferences');

  // 偶像列表資料與狀態
  const [idols, setIdols] = useState([]);
  const [total, setTotal] = useState(0);
  const [isLoading, setIsLoading] = useState(false);

  // 分頁邏輯
  const itemsPerPage = 12;
  const totalPages = Math.ceil(total / itemsPerPage);

  // 更新 URL 參數
  // 支援單一參數 (key, value) 或批次更新 (object)；navigateOptions 原樣傳給 setSearchParams（例如 { replace: true }）
  const updateFilters = (keyOrUpdates, value, navigateOptions) => {
    const newParams = new URLSearchParams(searchParams);

    // 支援 object 格式的批次更新
    const updates = typeof keyOrUpdates === 'object'
      ? keyOrUpdates
      : { [keyOrUpdates]: value };

    for (const [key, val] of Object.entries(updates)) {
      if (Array.isArray(val)) {
        newParams.delete(key);
        val.forEach(v => newParams.append(key, typeof v === 'object' ? v.value : v));
      } else if (val !== undefined && val !== null && val !== '') {
        newParams.set(key, val);
      } else {
        newParams.delete(key);
      }
    }

    newParams.set('page', '1'); // 重置頁碼
    setSearchParams(newParams, navigateOptions);
  };

  // 搜尋框：輸入先存本地，停頓後才寫回網址（replace，不留每個字的瀏覽紀錄），見 useDebouncedSearchInput
  const [keywordInput, setKeywordInput, keywordCompositionProps] = useDebouncedSearchInput(
    keyword,
    (value) => updateFilters('keyword', value, { replace: true })
  );

  // 重置所有篩選條件
  const handleClearFilters = () => {
    // 先清輸入框：網址的 keyword 若本來就是空的（剛打的字還在 debounce 中），清除後網址值沒變，
    // hook 不會同步回輸入框，debounce 到期後還會把剛打的字寫回網址
    setKeywordInput('');
    setSearchParams(new URLSearchParams({ page: '1' }));
  };

  const handlePageChange = (page) => {
    const newParams = new URLSearchParams(searchParams);
    newParams.set('page', page);
    // 換頁捲動交給 Pagination 的 scrollMode="window" 處理，避免跟 ScrollRestoration 的瞬間置頂互搶
    setSearchParams(newParams, { preventScrollReset: true });
  };

  const filters = {
    // 用輸入框的值而非網址值：網址最多比輸入框慢一個 debounce，手機版篩選 modal 開啟時會存一份快照、
    // 套用時連 keyword 一起寫回網址，用網址值會把剛打的字蓋掉（側欄沒有用到 keyword）
    keyword: keywordInput,
    gender,
    sexualOrientation,
    relationshipStatus,
    preferences,
    isOriginal,
    isNsfw,
    ageMin,
    ageMax
  };

  // 多選條件每次 render 都是新陣列，以序列化後的字串當依賴，內容不變就不重抓
  const genderKey = JSON.stringify(gender);
  const sexualOrientationKey = JSON.stringify(sexualOrientation);
  const relationshipStatusKey = JSON.stringify(relationshipStatus);
  const preferencesKey = JSON.stringify(preferences);

  // 取得偶像列表
  useEffect(() => {
    // 篩選條件改變後，前一次請求的回應較晚回來時丟棄，避免蓋掉新條件的結果
    let ignore = false;

    const fetchIdols = async () => {
      setIsLoading(true);
      try {
        const payload = {
          idol_id: idolId,
          keyword: keyword || '',
          age_min: ageMin,
          age_max: Math.min(ageMax || 100, 100),
          // 多選欄位改為直接傳陣列給後端
          gender: Array.isArray(gender) ? gender : [],
          sexual_orientation: Array.isArray(sexualOrientation) ? sexualOrientation : [],
          relationship_status: Array.isArray(relationshipStatus) ? relationshipStatus : [],
          preferences: Array.isArray(preferences) ? preferences : [],
          is_original: isOriginal !== '' ? Number(isOriginal) : null,
          is_nsfw: isNsfw !== '' ? Number(isNsfw) : null,
          limit: itemsPerPage,
          page: currentPage
        };

        const res = await Axios.post('/idol/get-idol-list', payload);
        if (ignore) return;
        const data = res.data;

        if (data.status_code === 'SYSTEM_1000') {
          setIdols(Array.isArray(data.data) ? data.data : []);
          setTotal(typeof data.total === 'number' ? data.total : 0);
        } else {
          setCurrentStatusCode(data.status_code);
          statusModalRef.current?.show();
          setIdols([]);
          setTotal(0);
        }
      } catch (error) {
        if (ignore) return;
        // NETWORK_ERROR 由全域 ServerErrorPage 處理，不另外顯示 modal
        if (error.httpErrorCode && error.httpErrorCode !== 'NETWORK_ERROR') {
          setCurrentStatusCode(error.httpErrorCode);
          statusModalRef.current?.show();
        } else if (!error.httpErrorCode) {
          console.error('Get idol list error:', error);
        }
        setIdols([]);
        setTotal(0);
      } finally {
        // 被丟棄的請求不關 loading：較新的請求還在途中
        if (!ignore) setIsLoading(false);
      }
    };

    fetchIdols();
    return () => {
      ignore = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    keyword,
    ageMin,
    ageMax,
    isOriginal,
    isNsfw,
    genderKey,
    sexualOrientationKey,
    relationshipStatusKey,
    preferencesKey,
    currentPage
  ]);

  return (
    <>
      <MainNav />
      <Container fluid="lg" className="nav-padding">
        <Row className="pt-4">
          <Col xs={12}>
            <h3 className="mb-4">{t('idols')}</h3>
          </Col>
        </Row>

        <Row>
          {/* 桌面版：左側篩選欄 */}
          <Col lg={3} className="d-none d-lg-block">
            <IdolFilterSidebar filters={filters} onFilterChange={updateFilters} onClearFilters={handleClearFilters} />
          </Col>

          {/* 右側內容區 */}
          <Col lg={9} xs={12}>
            {/* 篩選按鈕（手機版）+ 搜尋框 */}
            <div className="d-flex gap-2 align-items-center justify-content-lg-end mb-3">
              <button
                className="btn btn-primary flex-shrink-0 d-lg-none"
                onClick={() => setShowFilterModal(true)}
                style={{ minWidth: 'auto' }}
              >
                <FontAwesomeIcon icon={["fas", "filter"]} className="me-2" />
                {t('filter')}
              </button>

              <div className="position-relative flex-grow-1 flex-lg-grow-0">
                <FontAwesomeIcon
                  icon={["fas", "search"]}
                  className="position-absolute"
                  style={{ left: '15px', top: '50%', transform: 'translateY(-50%)', zIndex: 10 }}
                />
                <Form.Control
                  type="search"
                  placeholder={t('search')}
                  value={keywordInput}
                  onChange={(e) => setKeywordInput(e.target.value)}
                  {...keywordCompositionProps}
                  className="ps-5 py-2 rounded-5 border-0"
                />
              </div>
            </div>

            {/* IdolCard Grid */}
            {idols.length > 0 ? (
              <div className="cards-grid mb-4">
                {idols.map(idol => (
                  <IdolCard
                    key={idol.idol_uuid}
                    idol={{
                      // 這裡沿用 IdolCard 目前的 props 命名，將 API 的 idol_* 映射進去
                      subscribed_idol_id: idol.idol_id,
                      subscribed_idol_uuid: idol.idol_uuid,
                      subscribed_idol_name: idol.idol_nick_name,
                      subscribed_idol_is_official: idol.idol_is_official,
                      subscribed_idol_avatar_url:
                        idol.idol_big_avatar_url || placeholder_portrait_200x200,
                      subscribed_idol_cover_url:
                        idol.idol_cover_url || placeholder_cover_1600x700
                    }}
                    showSubscriptionInfo={false}
                    showOnline
                  />
                ))}
              </div>
            ) : (
              <div className="text-center py-5 text-muted">{t('no_matching_idols')}</div>
            )}

            {/* 分頁 */}
            {totalPages > 1 && (
              <div className="d-flex justify-content-center">
                <Pagination
                  total={total}
                  limit={itemsPerPage}
                  currentPage={currentPage}
                  onPageChange={handlePageChange}
                  scrollMode="window"
                />
              </div>
            )}
          </Col>
        </Row>
      </Container>

      {/* 手機版 Modal - 條件渲染避免 DOM 衝突 */}
      {showFilterModal && (
        <IdolFilterModal
          show={showFilterModal}
          onHide={() => setShowFilterModal(false)}
          filters={filters}
          onFilterChange={updateFilters}
        />
      )}

      {isLoading && <Loading />}
      <StatusModal ref={statusModalRef} statusCode={currentStatusCode} />
    </>
  );
};

export default IdolsPage;
